import React, { useState, useEffect, useRef, JSX } from "react";
import { getAuth, signOut, onAuthStateChanged, User } from "firebase/auth";
import { doc, onSnapshot, updateDoc, collection, getCountFromServer } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../firebase/firebase";
import { Dimensions, View, Text, TouchableOpacity, StyleSheet, Image, Modal, Pressable } from "react-native";
import { Menu, X } from "lucide-react";

import DailyDashboard from "./DailyDashboard";
import AnalyticsPage from "./analyticsScreen";
import OrdersPage from './SupportOrder';
import EcomProducts from './ProductList'
import Sellers from './sellers'
import Users from './userScreen'
import AddProduct from "./AddProduct";
import Branch from './branchScreen'
import Settings from './settingsScreen'
import Restaurant from "./restaurantScreen";
import Riders from "./riderScreen";
import RestaurantOrders from "./restaurantOrderScreen";
import Product from "./productScreen";
import Category from "./categoryScreen"
import SupportChat from "./SupportChat";
import ShareholderScreen from "./ShareholderScreen";
import RestaurantPayroll from './RestaurantPayroll';
import DeliveryPayroll from './DeliveryPayroll';
import CashCollection from './CashCollection';
import Photos from './PhotosScreen';
import PhotoOrganizer from './PhotoOrganizer';
import RestaurantMedia from './RestaurantMedia';
import RestaurantDbSync from './RestaurantDbSync';

const windowHeight = Dimensions.get("window").height;
const windowWidth = Dimensions.get("window").width;

const Icon = ({ name, size = 20, color = '#626c71' }: { name: string; size?: number; color?: string }) => (
  <span
    className="material-icons-round"
    style={{ fontSize: size, color, lineHeight: 1, userSelect: 'none' }}
  >
    {name}
  </span>
);

interface NavItem  { type: 'item';  key: string; label: string; icon: string }
interface NavGroup { type: 'group'; key: string; label: string; icon: string; children: { key: string; label: string; icon: string }[] }
type NavEntry = NavItem | NavGroup;

const NAV: NavEntry[] = [
  { type: 'item',  key: 'home',      label: 'Dashboard',  icon: 'dashboard' },
  { type: 'item',  key: 'analytics', label: 'Analytics',  icon: 'analytics' },
  {
    type: 'group', key: 'grp_orders', label: 'Orders', icon: 'receipt_long',
    children: [
      { key: 'Orders',       label: 'Live Orders', icon: 'bolt' },
      { key: 'RestaurantOrders', label: 'Restaurant Orders', icon: 'restaurant_menu' },
    ],
  },
  {
    type: 'group', key: 'grp_manage', label: 'Management', icon: 'manage_accounts',
    children: [
      { key: 'branch',       label: 'Branch',       icon: 'store' },
      { key: 'users',        label: 'Users',        icon: 'group' },
      { key: 'restaurant',   label: 'Restaurant',   icon: 'restaurant' },
      { key: 'riders',       label: 'Riders',       icon: 'two_wheeler' },
      { key: 'shareholder',  label: 'Shareholders', icon: 'account_balance' },
      { key: 'products',     label: 'Products',     icon: 'inventory_2' },
      { key: 'restaurantPayroll', label: 'Restaurant Payouts', icon: 'payments' },
      { key: 'deliveryPayroll',   label: 'Delivery Payroll',   icon: 'savings' },
      { key: 'cashCollection',    label: 'Cash Collection',    icon: 'account_balance_wallet' },
    ],
  },
  { type: 'item', key: 'supportChat', label: 'Support Chat', icon: 'chat' },
  { type: 'item', key: 'settings', label: 'Settings', icon: 'settings' },
];

const MOTIVATIONS = [
  'Your empire is expanding every day.',
  'Leaders build, others follow.',
  'Every order is a step toward greatness.',
  'You are the architect of this success.',
  'Vision fuels the engine of growth.',
  'Champions show up every day.',
  'Build it right, build it bold.',
  'Your hustle inspires the whole team.',
];

function calcAge(from: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - from.getTime();
  const years = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365));
  const months = Math.floor((diffMs % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
  const days = Math.floor((diffMs % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24));
  if (years >= 1) return `${years} yr${years > 1 ? 's' : ''} ${months} mo${months !== 1 ? 's' : ''}`;
  if (months >= 1) return `${months} month${months !== 1 ? 's' : ''} ${days} day${days !== 1 ? 's' : ''}`;
  return `${days} day${days !== 1 ? 's' : ''}`;
}

const WELCOME_MESSAGES = [
  'Every order you handle builds a legacy. Keep conquering! 🚀',
  'You are the reason Zapee delivers smiles. Thank you, Boss! 🌟',
  'The hustle never stops — and neither do you. Respect! 💪',
  'Great platforms are built by great people like you. Onward! 🏆',
  'Your work today feeds families tomorrow. That matters! ❤️',
  'Champions show up every single day. And here you are! 👑',
  'Each order is a promise kept. You make that happen! ⚡',
];

function getWelcomeMessage(): string {
  return WELCOME_MESSAGES[new Date().getDate() % WELCOME_MESSAGES.length];
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getMotivation(): string {
  return MOTIVATIONS[new Date().getDate() % MOTIVATIONS.length];
}

const AdminDashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("home");
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    grp_orders: true, grp_manage: true, grp_products: true,
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profileData, setProfileData] = useState<{ name?: string; profileImage?: string; branchId?: string } | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [welcomeOrderCount, setWelcomeOrderCount] = useState<number | null>(null);
  const [branchCreatedAt, setBranchCreatedAt] = useState<Date | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const auth = getAuth();
  const navigate = useNavigate();

  // Wait for Firebase to restore the session after a refresh.
  // auth.currentUser is null on first render even when the user is signed in.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthChecked(true);
      if (!user) navigate("/", { replace: true });
    });
    return () => unsub();
  }, [auth, navigate]);

  // Fetch shareholder profile for current user
  useEffect(() => {
    if (!authUser) return;
    const unsub = onSnapshot(doc(db, 'zapee_share_holders', authUser.uid), (snap) => {
      if (snap.exists()) setProfileData(snap.data() as any);
    });
    return () => unsub();
  }, [authUser]);

  // Fetch total order count for welcome modal
  useEffect(() => {
    getCountFromServer(collection(db, 'Orders'))
      .then(snap => setWelcomeOrderCount(snap.data().count))
      .catch(() => setWelcomeOrderCount(null));
  }, []);

  // Fetch branch created date for welcome modal age
  useEffect(() => {
    if (!profileData?.branchId) return;
    import('firebase/firestore').then(({ getDoc, doc: fsDoc }) => {
      getDoc(fsDoc(db, 'zapee_branches', profileData.branchId!)).then(snap => {
        if (!snap.exists()) return;
        const ts = snap.data()?.createdAt ?? snap.data()?.timestamp;
        if (!ts) return;
        // timestamp may be a Firestore Timestamp, number (ms), or date string
        let d: Date;
        if (ts && typeof ts === 'object' && 'toDate' in ts) {
          d = ts.toDate();
        } else if (typeof ts === 'number') {
          d = new Date(ts);
        } else {
          d = new Date(String(ts));
        }
        if (!isNaN(d.getTime())) setBranchCreatedAt(d);
      });
    });
  }, [profileData?.branchId]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = Dimensions.get('window').width < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };
    checkMobile();
    const subscription = Dimensions.addEventListener('change', checkMobile);
    return () => subscription?.remove();
  }, []);

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    try {
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();
      navigate("/");
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file || !authUser) return;
    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `Shareholder/${authUser.uid}_profile`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'zapee_share_holders', authUser.uid), { profileImage: url });
    } catch (e) { console.error(e); }
    finally { setUploadingPhoto(false); }
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const handleSectionSelect = (key: string) => {
    setActiveSection(key);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const sidebarWidth = isMobile ? 260 : (collapsed ? 60 : 220);

  const firstName = profileData?.name?.split(' ')[0] || 'Boss';

  if (!authChecked || !authUser) {
    return (
      <View style={[styles.layout, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text style={{ color: '#21808D', fontSize: 16, fontWeight: '600' }}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.layout}>

      {/* ── Welcome Modal ── */}
      <Modal
        visible={showWelcome}
        transparent
        animationType="fade"
        onRequestClose={() => setShowWelcome(false)}
      >
        <Pressable style={styles.welcomeOverlay} onPress={() => setShowWelcome(false)}>
          <Pressable style={styles.welcomeCard} onPress={e => e.stopPropagation()}>
            {/* Top glow strip */}
            <View style={styles.welcomeTopStrip} />

            {/* Logo circle */}
            <View style={styles.welcomeLogoRing}>
              <View style={styles.welcomeLogoInner}>
                <Text style={styles.welcomeLogoText}>Z</Text>
              </View>
            </View>

            {/* Greeting */}
            <Text style={styles.welcomeHi}>
              {getGreeting()}, {firstName}! 👋
            </Text>

            {/* Zapee intro */}
            <View style={styles.welcomeIntroBox}>
              <Text style={styles.welcomeIntroTitle}>Hi, I'm <Text style={{ color: '#21808D' }}>Zapee</Text> 🍽️</Text>
              <Text style={styles.welcomeIntroSub}>Your delivery empire, running strong for</Text>
              <Text style={styles.welcomeAge}>
                {branchCreatedAt ? calcAge(branchCreatedAt) : '—'}
              </Text>
            </View>

            {/* Stats row */}
            <View style={styles.welcomeStatsRow}>
              <View style={styles.welcomeStatBox}>
                <Text style={styles.welcomeStatNum}>
                  {welcomeOrderCount !== null ? welcomeOrderCount.toLocaleString() : '—'}
                </Text>
                <Text style={styles.welcomeStatLabel}>Total Orders</Text>
                <Text style={styles.welcomeStatCaption}>processed on platform</Text>
              </View>
            </View>

            {/* Inspiring message */}
            <Text style={styles.welcomeMessage}>{getWelcomeMessage()}</Text>

            {/* CTA */}
            <TouchableOpacity style={styles.welcomeBtn} onPress={() => setShowWelcome(false)}>
              <Text style={styles.welcomeBtnText}>Let's Go! ⚡</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <TouchableOpacity style={styles.overlay} onPress={() => setSidebarOpen(false)} activeOpacity={1} />
      )}

      {/* Sidebar */}
      <View style={[
        styles.sidebar,
        { width: sidebarWidth },
        isMobile && styles.sidebarMobile,
        sidebarOpen && isMobile && styles.sidebarOpen
      ]}>
        {/* Mobile Header */}
        <View style={styles.mobileHeader}>
          {!isMobile && !collapsed && (
            <Text style={styles.logoText}>ZAPEE</Text>
          )}
          <TouchableOpacity onPress={toggleSidebar} style={styles.menuButton}>
            {isMobile ? (sidebarOpen ? <X size={24} /> : <Menu size={24} />) : (collapsed ? <Menu size={20} /> : <X size={20} />)}
          </TouchableOpacity>
        </View>

        {/* Navigation */}
        {NAV.map((entry) => {
          if (entry.type === 'item') {
            const active = activeSection === entry.key;
            return (
              <TouchableOpacity
                key={entry.key}
                style={[
                  styles.sidebarItem,
                  active && styles.sidebarItemActive,
                  collapsed && !isMobile && styles.sidebarItemCollapsed,
                ]}
                onPress={() => handleSectionSelect(entry.key)}
              >
                <View style={styles.sidebarItemContent}>
                  <Icon name={entry.icon} size={20} color={active ? '#fff' : '#626c71'} />
                  {(!collapsed || isMobile) && (
                    <Text style={[styles.sidebarLabel, active && styles.sidebarLabelActive]}>
                      {entry.label}
                    </Text>
                  )}
                </View>
              </TouchableOpacity>
            );
          }

          // Group
          const isOpen = openGroups[entry.key];
          const hasActive = entry.children.some(c => c.key === activeSection);
          const toggleGroup = () =>
            setOpenGroups(prev => ({ ...prev, [entry.key]: !prev[entry.key] }));

          return (
            <View key={entry.key}>
              {/* Group header */}
              <TouchableOpacity
                style={[
                  styles.groupHeader,
                  hasActive && styles.groupHeaderActive,
                  collapsed && !isMobile && styles.sidebarItemCollapsed,
                ]}
                onPress={collapsed && !isMobile ? undefined : toggleGroup}
              >
                <View style={styles.sidebarItemContent}>
                  <Icon name={entry.icon} size={20} color={hasActive ? '#21808D' : '#626c71'} />
                  {(!collapsed || isMobile) && (
                    <>
                      <Text style={[styles.groupLabel, hasActive && styles.groupLabelActive]}>
                        {entry.label}
                      </Text>
                      <Text style={styles.chevron}>{isOpen ? '▾' : '▸'}</Text>
                    </>
                  )}
                </View>
              </TouchableOpacity>

              {/* Children */}
              {(!collapsed || isMobile) && isOpen && entry.children.map(child => {
                const childActive = activeSection === child.key;
                return (
                  <TouchableOpacity
                    key={child.key}
                    style={[styles.childItem, childActive && styles.childItemActive]}
                    onPress={() => handleSectionSelect(child.key)}
                  >
                    <View style={styles.childItemContent}>
                      <View style={[styles.childDot, childActive && styles.childDotActive]} />
                      <Text style={[styles.childLabel, childActive && styles.childLabelActive]}>
                        {child.label}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        })}

        {/* Spacer */}
        <View style={{ flex: 1 }} />

        {/* Logout button */}
        <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
          <Icon name="logout" size={20} color="#fff" />
          {(!collapsed || isMobile) && <Text style={styles.logoutText}>Logout</Text>}
        </TouchableOpacity>
      </View>

      {/* Main Content */}
      <View style={styles.mainContent}>
        {/* Top Bar */}
        <View style={styles.topBar}>
          <View style={styles.topBarContent}>
            {/* Mobile menu button - always visible on mobile */}
            {isMobile && (
              <TouchableOpacity onPress={toggleSidebar} style={styles.mobileMenuButton}>
                <Menu size={24} color="#333" />
              </TouchableOpacity>
            )}

            {/* Boss greeting */}
            <View style={styles.greeting}>
              <Text style={styles.greetingName} numberOfLines={1}>
                {getGreeting()}, {profileData?.name?.split(' ')[0] || 'Boss'} 👑
              </Text>
              {!isMobile && (
                <Text style={styles.greetingMotivation} numberOfLines={1}>
                  {getMotivation()}
                </Text>
              )}
            </View>

            <TouchableOpacity style={styles.userInfo} onPress={() => setProfileMenuOpen(true)}>
              {profileData?.profileImage ? (
                <Image source={{ uri: profileData.profileImage }} style={styles.avatarImage} />
              ) : (
                <View style={styles.avatarCircle}>
                  <Text style={styles.avatarText}>
                    {(profileData?.name || authUser?.email || 'A').charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
            </TouchableOpacity>

            {/* Profile Dropdown Modal */}
            <Modal
              visible={profileMenuOpen}
              transparent
              animationType="fade"
              onRequestClose={() => setProfileMenuOpen(false)}
            >
              <Pressable style={styles.menuOverlay} onPress={() => setProfileMenuOpen(false)}>
                <View style={styles.profileMenu}>
                  {/* Profile Header */}
                  <View style={styles.profileMenuHeader}>
                    {profileData?.profileImage ? (
                      <Image source={{ uri: profileData.profileImage }} style={styles.profileMenuAvatar} />
                    ) : (
                      <View style={[styles.avatarCircle, styles.profileMenuAvatar]}>
                        <Text style={styles.avatarText}>
                          {(profileData?.name || authUser?.email || 'A').charAt(0).toUpperCase()}
                        </Text>
                      </View>
                    )}
                    <View style={{ flex: 1 }}>
                      <Text style={styles.profileMenuName} numberOfLines={1}>
                        {profileData?.name || 'Admin'}
                      </Text>
                      <Text style={styles.profileMenuEmail} numberOfLines={1}>
                        {authUser?.email || ''}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.profileMenuDivider} />

                  {/* Change Photo */}
                  <View style={styles.profileMenuItem}>
                    <View style={{ position: 'relative' }}>
                      <Text style={styles.profileMenuAction}>
                        {uploadingPhoto ? 'Uploading...' : '📷  Change Photo'}
                      </Text>
                      <input
                        ref={photoInputRef as any}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange as any}
                        style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>

                  <View style={styles.profileMenuDivider} />

                  <TouchableOpacity style={styles.profileMenuItem} onPress={handleLogout}>
                    <Text style={styles.profileMenuLogout}>Sign Out</Text>
                  </TouchableOpacity>
                </View>
              </Pressable>
            </Modal>
          </View>
        </View>

        {/* Content Area */}
        <View style={styles.contentArea}>
          {activeSection === "home"      && <DailyDashboard branchId={profileData?.branchId ?? ''} />}
          {activeSection === "analytics" && <AnalyticsPage branchId={profileData?.branchId ?? ''} />}
          {activeSection === "users" && <Users />}
          {activeSection === "seller" && <Sellers />}
          {activeSection === "products" && <Product branchId={profileData?.branchId ?? ''} />}
          {activeSection === "createProducts" && <AddProduct />}
          {activeSection === "restaurant" && <Restaurant branchId={profileData?.branchId ?? ''} />}
          {activeSection === "riders" && <Riders />}
          {activeSection === "category" && <Category />}
          {activeSection === "branch" && <Branch />}
          {activeSection === "productList" && <EcomProducts />}
          {activeSection === "Orders" && <OrdersPage />}
          {activeSection === "RestaurantOrders" && <RestaurantOrders />}
          {activeSection === "supportChat" && <SupportChat />}
          {activeSection === "settings" && <Settings branchId={profileData?.branchId ?? ''} />}
          {activeSection === "shareholder" && <ShareholderScreen />}
          {activeSection === "restaurantPayroll" && <RestaurantPayroll />}
          {activeSection === "deliveryPayroll" && <DeliveryPayroll />}
          {activeSection === "cashCollection" && <CashCollection />}
          {activeSection === "photos" && <Photos />}
          {activeSection === "photoOrganizer" && <PhotoOrganizer />}
          {activeSection === "restaurantMedia" && <RestaurantMedia />}
          {activeSection === "dbSync" && <RestaurantDbSync />}
        </View>
      </View>

      {/* ── Vertical Right Bar (ZAPEE) ── */}
      <View style={isMobile ? styles.rightBarMobile : styles.rightBar}>
        <View style={styles.rightBarTopDot} />
        {!isMobile && (
          <View style={styles.rightBarBadge}>
            <Text style={styles.rightBarBadgeText}>LIVE</Text>
          </View>
        )}
        <View style={styles.rightBarTextContainer}>
          <Text style={styles.rightBarText}>Z A P E E</Text>
        </View>
        {!isMobile && (
          <View style={styles.rightBarFooter}>
            <Text style={styles.rightBarVersion}>v2.4</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  rightBar: {
    width: 44,
    backgroundColor: '#0F172A',
    borderLeftWidth: 1,
    borderLeftColor: '#1E293B',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 18,
    zIndex: 90,
  },
  rightBarMobile: {
    position: 'absolute',
    right: 0,
    top: '38%',
    width: 26,
    height: 130,
    backgroundColor: '#0F172A',
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    zIndex: 990,
    shadowColor: '#000',
    shadowOffset: { width: -2, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 10,
  },
  rightBarTopDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#10B981',
  },
  rightBarBadge: {
    backgroundColor: 'rgba(33, 128, 141, 0.25)',
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#21808D',
    marginTop: 6,
  },
  rightBarBadgeText: {
    color: '#2DD4BF',
    fontSize: 8,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  rightBarTextContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightBarText: {
    fontSize: 12,
    fontWeight: '900',
    color: '#38BDF8',
    letterSpacing: 5,
    writingMode: 'vertical-rl',
    textTransform: 'uppercase',
    userSelect: 'none',
  } as any,
  rightBarFooter: {
    alignItems: 'center',
  },
  rightBarVersion: {
    fontSize: 8,
    fontWeight: '700',
    color: '#64748B',
    letterSpacing: 0.5,
  },
  layout: {
    flex: 1,
    flexDirection: 'row',
    height: windowHeight,
    overflow: 'hidden',
    backgroundColor: '#f6f8fb',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 998,
  },
  sidebar: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    paddingHorizontal: 12,
    flexDirection: 'column',
    height: windowHeight,
    overflow: 'scroll',
    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
  },
  sidebarMobile: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    height: '100%',
    zIndex: 999,
    backgroundColor: '#ffffff',
    transform: [{ translateX: -280 }],
  },
  sidebarOpen: {
    transform: [{ translateX: 0 }],
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingHorizontal: 8,
  },
  logoText: {
    fontSize: 22,
    fontWeight: '800',
    color: '#21808D',
    letterSpacing: 2,
  },
  menuButton: {
    padding: 8,
    borderRadius: 8,
  },
  sidebarItem: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginBottom: 2,
    borderRadius: 10,
    backgroundColor: '#ffffff',
  },
  sidebarItemActive: {
    backgroundColor: '#21808D',
  },
  sidebarItemCollapsed: {
    paddingVertical: 14,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sidebarItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  sidebarLabel: {
    color: '#1a2332',
    fontSize: 14,
    flex: 1,
  },
  sidebarLabelActive: {
    color: '#ffffff',
    fontWeight: '600',
  },
  // Group
  groupHeader: {
    width: '100%',
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginBottom: 1,
    borderRadius: 10,
    backgroundColor: '#ffffff',
  },
  groupHeaderActive: {
    backgroundColor: '#f0f9fa',
  },
  groupLabel: {
    color: '#1a2332',
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  groupLabelActive: {
    color: '#21808D',
  },
  chevron: {
    fontSize: 11,
    color: '#94a3b8',
    marginLeft: 2,
  },
  // Children
  childItem: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    paddingLeft: 32,
    borderRadius: 8,
    marginBottom: 1,
  },
  childItemActive: {
    backgroundColor: '#e8f5f7',
  },
  childItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  childDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#cbd5e1',
  },
  childDotActive: {
    backgroundColor: '#21808D',
  },
  childLabel: {
    fontSize: 13,
    color: '#475569',
  },
  childLabelActive: {
    color: '#21808D',
    fontWeight: '600',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#21808D',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 20,
    marginHorizontal: 4,
  },
  logoutText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  topBar: {
    height: 60,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  topBarContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileMenuButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  userEmail: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  avatarCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#21808D',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  avatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: '#21808D',
  },
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.2)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: 64,
    paddingRight: 16,
  },
  profileMenu: {
    backgroundColor: '#fff',
    borderRadius: 14,
    width: 240,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    overflow: 'hidden',
  },
  profileMenuHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
  },
  profileMenuAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  profileMenuName: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111',
  },
  profileMenuEmail: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  profileMenuDivider: {
    height: 1,
    backgroundColor: '#f0f0f0',
  },
  profileMenuItem: {
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  profileMenuAction: {
    fontSize: 15,
    fontWeight: '600',
    color: '#0288d1',
  },
  profileMenuLogout: {
    fontSize: 15,
    fontWeight: '600',
    color: '#e53935',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    height: windowHeight,
    overflow: 'hidden',
  },
  contentArea: {
    flex: 1,
    overflow: 'scroll',
  },

  // Greeting
  greeting: {
    flex: 1,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  greetingName: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a2332',
    lineHeight: 22,
  },
  greetingMotivation: {
    fontSize: 12,
    color: '#21808D',
    fontWeight: '500',
    marginTop: 1,
    fontStyle: 'italic',
  },

  // Footer
  footer: {
    height: 36,
    backgroundColor: '#1a2332',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    justifyContent: 'space-between',
  },
  footerLeft: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: '500',
  },
  footerCenter: {
    fontSize: 11,
    color: '#21808D',
    fontWeight: '600',
    fontStyle: 'italic',
  },
  footerRight: {
    fontSize: 11,
    color: '#64748b',
  },

  // ── Welcome Modal ────────────────────────────────────────────────
  welcomeOverlay: {
    flex: 1,
    backgroundColor: 'rgba(10,20,40,0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  welcomeCard: {
    backgroundColor: '#ffffff',
    borderRadius: 24,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    overflow: 'hidden',
    paddingBottom: 28,
    shadowColor: '#21808D',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 12,
  },
  welcomeTopStrip: {
    width: '100%',
    height: 6,
    backgroundColor: '#21808D',
  },
  welcomeLogoRing: {
    marginTop: 28,
    marginBottom: 6,
    width: 84,
    height: 84,
    borderRadius: 42,
    borderWidth: 3,
    borderColor: '#21808D',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0fafa',
  },
  welcomeLogoInner: {
    width: 66,
    height: 66,
    borderRadius: 33,
    backgroundColor: '#21808D',
    justifyContent: 'center',
    alignItems: 'center',
  },
  welcomeLogoText: {
    fontSize: 32,
    fontWeight: '900',
    color: '#ffffff',
    letterSpacing: 1,
  },
  welcomeHi: {
    fontSize: 22,
    fontWeight: '800',
    color: '#1a2332',
    marginTop: 10,
    marginBottom: 2,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  welcomeIntroBox: {
    alignItems: 'center',
    marginTop: 14,
    marginBottom: 6,
    paddingHorizontal: 24,
  },
  welcomeIntroTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1a2332',
    marginBottom: 4,
    textAlign: 'center',
  },
  welcomeIntroSub: {
    fontSize: 13,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 2,
  },
  welcomeAge: {
    fontSize: 20,
    fontWeight: '800',
    color: '#21808D',
    letterSpacing: 0.5,
    marginTop: 2,
  },
  welcomeStatsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 18,
    marginBottom: 4,
    paddingHorizontal: 20,
    gap: 12,
  },
  welcomeStatBox: {
    backgroundColor: '#f0fafa',
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 32,
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#b2e0e4',
  },
  welcomeStatNum: {
    fontSize: 28,
    fontWeight: '900',
    color: '#21808D',
    letterSpacing: 0.5,
  },
  welcomeStatLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#1a2332',
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  welcomeStatCaption: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 1,
  },
  welcomeMessage: {
    fontSize: 14,
    fontStyle: 'italic',
    color: '#475569',
    textAlign: 'center',
    paddingHorizontal: 28,
    marginTop: 18,
    marginBottom: 20,
    lineHeight: 20,
  },
  welcomeBtn: {
    backgroundColor: '#21808D',
    borderRadius: 50,
    paddingVertical: 13,
    paddingHorizontal: 48,
    shadowColor: '#21808D',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 10,
    elevation: 6,
  },
  welcomeBtnText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
});

export default AdminDashboard;